import { Component, ContentChild, ElementRef, EventEmitter, Input, OnInit, Output, SimpleChanges, TemplateRef } from '@angular/core';
// import { UtilsService } from '../../services/utils.service';

@Component({
  selector: 'app-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss']
})
export class ListComponent implements OnInit {

  constructor(
    private elementRef: ElementRef,
    // private utilsService: UtilsService
  ) { }

  /**
   * Variable to control the loading status of the list.
   */
  public loading: boolean = false;

  /**
   * Data input from the parent component to display.
   *
   * This variable contains all the values that the parent sends to the component.
   */
  @Input() data: any[] = [];

  /**
   * Actual list displayed from the list.
   *
   * This is used to display paginated data.
   */
  listToDisplay: any[] = [];

  /**
   * Flag to enable/disable pagination.
   *
   * Disabled by default if no value is specified.
   */
  @Input() paginationEnabled: boolean = false;

  /**
   * List of fields. Must contain at least 1 field.
   * Structure => {name: string, type: string, property: string, condition: string}[]
   *
   * name is the name of the field (human readable). Mandatory.
   *
   * type is the type of field: string | datetime | number | button | custom. Mandatory.
   *
   * property is the actual field in the data object. Mandatory.
   *
   * condition is a field name in which the button visibility relies on. Optional.
   *
   * A custom field is one where custom HTML code can be sent via content projection (https://angular.io/guide/content-projection).
   *
   * To use the custom field put the custom html code inside the <app-list> tag and inside this
   * <ng-template let-i #customField></ng-template>. The variable i controls the index of the list
   * and the #customField property tells the list where is the code intended to go.
   */
  @Input() fields: { name: string, type: string, property: string, condition?: string }[] = [];

  /**
   * List of fields that are sortable.
   */
  @Input() sortableFields: String[] = [];

  /**
   * Flag to enable/disable the clickable rows.
   *
   * Disabled by default if no value is specified.
   */
  @Input() clickableRows: boolean = false;

  /**
   * Variable that holds the current field in which the list is sorted.
   */
  currentField: String = "";

  /**
   * Variable that holds the current direction of sorting from a field.
   *
   * Direction can be 1 (descending) or -1 (ascending).
   */
  currentDirection: number = 1;

  /**
   *
   */
  @Input() paginate: boolean = false;

  /**
   * Number of pages that the data is divided in.
   *
   * Calculated on init.
   */
  pages: number = 0;

  /**
   * Variable to hold the number of elements per page. 100 elements per page by default.
   */
  @Input() elementsPerPage: number = 100;

  /**
   * Variable that holds the current page number.
   *
   * Pages go from 1 to "pages".
   */
  currentPage: number = 1;

  /**
   * Index of the first element in the displayed list with respect with the data list.
   *
   * Used for pagination.
   */
  lowerIndex: number = 0;

  /**
   * Index of the last element in the displayed list with respect with the data list.
   *
   * Used for pagination.
   */
  upperIndex: number = 0;

  /**
   * Page number where the middle elements of the page selection control start.
   */
  middleRangeStart: number = 0;

  /**
   * Page number where the middle elements of the page selection control end.
   */
  middleRangeEnd: number = 0;

  /**
   * Flag to set visibility of the left ellipsis in the page selection control.
   */
  ellipsisLeft: boolean = false;

  /**
   * Flag to set visibility of the right ellipsis in the page selection control.
   */
  ellipsisRight: boolean = false;

  /**
   * Checkbox to select all the elements in the page.
   */
  selectAllCheckbox: boolean = false;

  /**
   * For reference check: https://stackoverflow.com/questions/35190188/can-an-ng-content-be-used-inside-of-an-ngfor/60725235#60725235
   *
   * This variable allows to insert multiple html content into the custom field.
   *
   * For now, the list only allows 1 custom field.
   */
  @ContentChild('customField', { static: false }) customFieldTemplateRef: TemplateRef<any>;

  /**
   * Event emitter for a button clicked in the list.
   *
   * Triggered by a button field in the list.
   */
  @Output() buttonClickedEmitter = new EventEmitter<any>();

  /**
   * Event emitter for a row click.
   *
   * Triggered when a row is clicked and the property clickableRows is true.
   */
  @Output() rowClickedEmitter = new EventEmitter<any>();

  /**
   * Event emitter for a change in a checkbox.
   *
   * Triggered when a single checkbox is changed in a checkbox field or when the checkbox to select all the rows is changed.
   */
  @Output() checkboxChangedEmitter = new EventEmitter<any>();

  ngOnInit(): void {
    this.elementRef.nativeElement.ownerDocument.body.style.overflow = 'auto';
    this.initList();
  }

  /**
   * Initializes the component by:
   *
   * - Calculating the variables related to pagination if the paginate property is true.
   *
   * - Builds the listToDisplay by calling goToPage(1) if the pagination is enabled or directly setting the incoming data into the listToDisplay variable.
   *
   * - Checks if all items are selected to modify the checkbox that selects/unselects all rows.
   */
  initList() {
    if (this.paginate) {
      this.pages = Math.ceil(this.data.length / this.elementsPerPage);
      this.currentPage = 1;
      this.lowerIndex = 0;
      this.upperIndex = this.elementsPerPage;
    }

    if (this.paginate) {
      this.goToPage(1);
    } else {
      this.listToDisplay = this.data;
    }
    this.checkIfAllItemsSelected();
  }

  /**
   * Check if all the checkboxes in the list are selected.
   */
  checkIfAllItemsSelected() {
    let allChecked = true && this.listToDisplay.length > 0;
    this.listToDisplay.forEach((element: any) => {
      allChecked = allChecked && element.selected;
    });
    this.selectAllCheckbox = allChecked;
  }

  /**
   * Method to return an array of a range specified.
   *
   * This builds an array of the form [init, init+1, ... , end-1, end].
   * @param init starting number of the range.
   * @param end final number of the range.
   * @returns Array of the form [init, init+1, ... , end-1, end].
   */
  range(init: number, end: number) {
    let arr = [];
    for (let i = init; i <= end; i++) {
      arr.push(i);
    }
    return arr;
  }

  /**
   * Changes the page displayed on the list. This controls also all the conditions and constraints that the
   * list have.
   * @param page The page number. Pages start in 1.
   */
  goToPage(page: number) {
    if (page <= this.pages && page > 0) {
      this.currentPage = page;
      this.lowerIndex = this.elementsPerPage * (this.currentPage - 1);
      this.upperIndex = this.lowerIndex + this.elementsPerPage;
      this.listToDisplay = this.data.slice(this.lowerIndex, this.upperIndex);
      this.ellipsisLeft = this.currentPage >= 6;
      this.middleRangeStart = this.currentPage - 3;
      if (this.middleRangeStart <= 1) this.middleRangeStart = 2;
      this.middleRangeEnd = this.currentPage + 3;
      if (this.middleRangeEnd >= this.pages) this.middleRangeEnd = this.pages - 1;
      this.ellipsisRight = this.middleRangeEnd + 1 < this.pages;

    } else {
      this.listToDisplay = [];
    }
    this.checkIfAllItemsSelected();
  }

  /**
   * Sets the field that is currently selected for sorting.
   * @param field The field selected for sorting.
   */
  public setCurrentField(field: String) {
    this.currentField = field;
  }

  /**
   * Set a specific sorting direction of the list.
   * @param direction The sorting direction. Direction can be 1 (descending) or -1 (ascending).
   */
  public setCurrentDirection(direction: number) {
    this.currentDirection = direction;
  }

  /**
   * Reload the list on changes done in the parent component.
   * @param changes SimpleChanges object with the changes.
   */
  ngOnChanges(changes: SimpleChanges): void {
    this.initList();
  }

  /**
   * Reload list by sorting is and changing a variable (loading) to trigger the reload of variables in angular.
   */
  public reload() {
    this.sortList(this.currentField, this.currentDirection);

    // Strategy to reload the component is to hide it and show it again.
    this.loading = true;
    setTimeout(() => this.loading = false);
  }

  /**
   * Emits an event on a button click. This is for fields that are of type "button".
   * @param data Data to send to the recipient of the event.
   * @param emitter Element that triggered the click event.
   */
  buttonClicked(data: any[], emitter: any) {
    let dataToSend = {
      data: data,
      emitter: emitter
    }
    this.buttonClickedEmitter.emit(dataToSend);
  }

  /**
   * Triggers the rowClickedEmitter event when a row is clicked and the property clickableRows is true.
   *
   * This send the entire row element as the $event data parameter.
   * @param rowData Complete row data element.
   */
  rowClicked(rowData: any): void {
    if (this.clickableRows) {
      this.rowClickedEmitter.emit(rowData);
    }
  }

  /**
   * Triggers the checkboxChangedEmitter event when a checkbox state is changed.
   *
   * This send the entire row element as the $event data parameter.
   * @param rowData
   */
  checkboxChanged(rowData: any): void {
    rowData.selected = !rowData.selected;
    this.checkIfAllItemsSelected();
    this.checkboxChangedEmitter.emit(rowData);
  }

  /**
   * Method to select or deselect all checkboxes in the list page. This also emits checkboxChangedEmitter.
   */
  checkAllItems() {
    let valueToSet = true; //Check them all by default
    if (this.selectAllCheckbox) { //If all values checked, unselect them
      valueToSet = false;
    }

    this.listToDisplay.forEach((element: any) => {
      element.selected = valueToSet;
    });

    this.selectAllCheckbox = valueToSet;
    this.checkboxChangedEmitter.emit();
  }

  /**
   * Returns the value for the specified property in item, even when the property is into another property.
   * For example if the item is {a: {b: "value"}}, and the property is "a.b", it returns the value "value".
   * @param item Item to extract the value from
   * @param property Property name. Can be nested. For example "a.b" can be specified to access the propery "a" and then its propery "b".
   * @returns The value of "property" in the "item".
   */
  resolveObjectProperty(item: any, property: String) {
    
    try {
      let propertySequence = property.split(".");
      let objectProperty = item;
      for(let prop in propertySequence){
        objectProperty = objectProperty[propertySequence[prop]];
      }
      
      return objectProperty;  
    } catch (error) {
      return "";
    }

  }

  /**
   * Check a condition for a field.
   *
   * This hides or disables a field element if the condition is not met.
   * @param item item from the condition is calculated.
   * @param field The field that the condition affects.
   * @returns true in case there are no conditions or the condition boolean itself.
   */
  checkCondition(item: any, field: any) {
    if ("condition" in field) {
      return item[field.condition];
    }
    return true;
  }

  /**
   * Sorts the complete list by a field and a direction specified as parameters
   * @param field Field in which the list will be sorted.
   * @param direction Direction of sorting. Direction can be 1 (descending) or -1 (ascending).
   */
  sortList(field: String, direction: number): void {
    let index = this.sortableFields.indexOf(field);
    if (index == -1) return;

    // Direction can be 1 (descending) or -1 (ascending)
    if (direction != 1 && direction != -1) return;

    this.data = this.data.sort((a: any, b: any) => {
      if (direction == 1) {
        if (a.get(field) > b.get(field)) return 1;
        if (a.get(field) == b.get(field)) return 0;
        if (a.get(field) < b.get(field)) return -1;
      } else {
        if (a.get(field) > b.get(field)) return -1;
        if (a.get(field) == b.get(field)) return 0;
        if (a.get(field) < b.get(field)) return 1;
      }
      return 0;
    });
  }

}
